import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client';
import { client } from './apollo';
import { AuthProvider, useAuth } from './context/AuthContext';
import { loginPathFor } from './context/signedInState';
import { AccountProvider } from './context/AccountContext';
import { ProductProvider, useProduct, PRODUCT_HOME } from './context/ProductContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProductRoute } from './components/common/ProductRoute';
import { SidebarProvider } from './context/SidebarContext';
import { ProfileSelectionProvider } from './context/ProfileSelectionContext';
import { Login } from './components/account/Login';
import { SignUp } from './components/account/SignUp';
import PaymentPage from './components/account/PaymentPage';
import OrderConfirmationPage from './components/account/OrderConfirmationPage';
import ConnectWordPress from './components/account/ConnectWordPress';
import { Dashboard } from './components/layouts/Dashboard';
import { SettingsLayout } from './components/layouts/Settings';
import { ProfilePage } from './components/settings/Profile';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import DataDeletionStatus from './components/account/DataDeletionStatus';
import TwitterCallback from './components/auth/TwitterCallback';
import FacebookCallback from './components/auth/FacebookCallback';
import ThreadsCallback from './components/auth/ThreadsCallback';
import InstagramCallback from './components/auth/InstagramCallback';
import YouTubeCallback from './components/auth/YouTubeCallback';
import GoogleAnalyticsCallback from './components/auth/GoogleAnalyticsCallback';
import TikTokCallback from './components/auth/TikTokCallback';
import LinkedInCallback from './components/auth/LinkedInCallback';
import SnapchatCallback from './components/auth/SnapchatCallback';
import { ProjectsLayout } from './components/layouts/Projects';
import { ResearchLayout } from './components/layouts/Research';
import { AssemblerLayout } from './components/layouts/Assembler';
import { ContentLayout } from './components/layouts/Content';
import { ContentDetail } from './components/layouts/ContentDetail';
import { MediaLibrary } from './components/layouts/MediaLibrary';
import { CollectionLayout } from './components/layouts/Collection';
import { CaptureLayout } from './components/layouts/Capture';
import { CollectionItemDetail } from './components/layouts/CollectionItemDetail';
import { AppShell } from './components/nav/AppShell';
import { ManageRedirect } from './components/cms/manageRedirects';
import { CmsFormSettings } from './components/cms/CmsFormSettings';
import { ContentSettings } from './components/layouts/ContentSettings';
import { PageLoader } from './components/common/PageLoader';
import SiteSignIn from './components/auth/SiteSignIn';
import { CmsToday } from './components/cms/CmsToday';
import { CmsSites } from './components/cms/CmsSites';
import { AtomizeSection, CampaignsSection, ReachSection } from './components/cms/Sections';
import { PrismAiSettings } from './components/cms/PrismAiSettings';
import { AddonsRedirect, MovedTo, SiteRedirect } from './components/cms/redirects';
import { ApprovalsLink, OldApprovalLink } from './components/approvals/ApprovalsLink';
import { ApprovalsProvider } from './components/approvals/ApprovalsReview';
import { ReachCalendar } from './components/reach/ReachCalendar';
import { ReachPosts } from './components/reach/ReachPosts';
import { ReachCampaigns } from './components/reach/ReachCampaigns';
import { CampaignCalendar } from './components/reach/CampaignCalendar';
import { CampaignAnalytics } from './components/reach/CampaignAnalytics';
import { UploadRequestPage } from './components/media/UploadRequestPage';
import { ReachChannels } from './components/reach/ReachChannels';
import { ReachAnalytics } from './components/reach/ReachAnalytics';

/** Old tinycms article URLs open the one article page. */
const CmsArticleRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/content/${id}` : '/content'} replace />;
};

/** Signed-in routes share the Ink top bar. Login and callbacks stay outside it.
 *  A blocked visit hands its destination to login via router state, not
 *  sessionStorage, so the notice stays on this navigation. */
const AuthedShell: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  // Before the top bar exists: the whole window.
  if (isLoading) return <PageLoader variant="screen" />;
  if (!isAuthenticated) {
    const from = location.pathname + location.search;
    // The return page rides in the sign-in address too, so a reloaded or
    // restored tab still comes back here (signedInState).
    return <Navigate to={loginPathFor(from)} replace state={{ from }} />;
  }
  return (
    <ApprovalsProvider>
      <AppShell />
    </ApprovalsProvider>
  );
};

// Collect's media library. Atomizer's library lives inside Content.
const MediaRoute: React.FC = () => {
  const { product, isLoading } = useProduct();
  if (isLoading) return null;
  if (product === 'atomizer') return <Navigate to="/content?view=media" replace />;
  return <MediaLibrary />;
};

// The app's landing route belongs to the active shell: Reach's Today
// dashboard, or the Content Engine library.
const HomeRoute: React.FC = () => {
  const { product, isLoading } = useProduct();
  if (isLoading) return <PageLoader />;
  // Every shell but TinySocial lands on its own home (tinycms → /today,
  // Collect → /collection).
  if (product !== 'reach') {
    return <Navigate to={PRODUCT_HOME[product]} replace />;
  }
  return (
    <ProductRoute product="reach">
      <Dashboard />
    </ProductRoute>
  );
};

// Routing outside /account is owned by the edge (Cloudflare in production,
// scripts/dev-edge.js locally), which serves the marketing sites. The app never
// handles those paths.
function App() {
  return (
    <ApolloProvider client={client}>
      <AuthProvider>
        <ThemeProvider>
          <AccountProvider>
            <ProductProvider>
              <SidebarProvider>
                <ProfileSelectionProvider>
                  <ErrorBoundary>
                    <BrowserRouter basename="/account">
                      <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<SignUp />} />
                        <Route path="/reader-sign-in" element={<SiteSignIn />} />
                        {/* An agent's request_media_upload link: signed in, own page. */}
                        <Route path="/upload/:token" element={<UploadRequestPage />} />
                        <Route path="/data-deletion/:code" element={<DataDeletionStatus />} />
                        {/* WordPress plugin connect landing (handles its own auth to
                        preserve the pairing query params). */}
                        <Route path="/connect" element={<ConnectWordPress />} />
                        <Route path="/payment" element={<PaymentPage />} />
                        <Route path="/order-confirmation" element={<OrderConfirmationPage />} />
                        <Route path="/auth/twitter/callback" element={<TwitterCallback />} />
                        <Route path="/auth/facebook/callback" element={<FacebookCallback />} />
                        <Route path="/auth/threads/callback" element={<ThreadsCallback />} />
                        <Route path="/auth/instagram/callback" element={<InstagramCallback />} />
                        <Route path="/auth/youtube/callback" element={<YouTubeCallback />} />
                        <Route path="/auth/tiktok/callback" element={<TikTokCallback />} />
                        <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
                        <Route path="/auth/snapchat/callback" element={<SnapchatCallback />} />
                        <Route
                          path="/auth/google-analytics/callback"
                          element={<GoogleAnalyticsCallback />}
                        />
                        <Route element={<AuthedShell />}>
                          {/* Landing route: Reach's Today, or the Engine library. */}
                          <Route path="/" element={<HomeRoute />} />
                          {/* An agent's approval link (MCP approvalUrl): the Approvals
                            review, open on that item, over Today. */}
                          <Route path="/approvals/:kind/:id" element={<ApprovalsLink />} />
                          {/* Links agents sent before the review land there too. */}
                          <Route
                            path="/agents/approve/:id"
                            element={<OldApprovalLink kind="agent" />}
                          />
                          {/* ── Reach shell ─────────────────────────────────── */}
                          <Route
                            path="/reach/calendar"
                            element={
                              <ReachSection>
                                <ReachCalendar />
                              </ReachSection>
                            }
                          />
                          {/* Every post in one table (backlog 4e7c78c8). */}
                          <Route
                            path="/reach/posts"
                            element={
                              <ReachSection>
                                <ReachPosts />
                              </ReachSection>
                            }
                          />
                          <Route
                            path="/reach/approve/:postId"
                            element={<OldApprovalLink kind="post" />}
                          />
                          {/* Campaigns is top level (backlog bf819be4); Reach keeps its
                            always-on sharing rules as Automatic. */}
                          <Route
                            path="/campaigns"
                            element={
                              <CampaignsSection>
                                <ReachCampaigns mode="campaigns" />
                              </CampaignsSection>
                            }
                          />
                          <Route
                            path="/campaigns/calendar"
                            element={
                              <CampaignsSection>
                                <CampaignCalendar />
                              </CampaignsSection>
                            }
                          />
                          <Route
                            path="/campaigns/analytics"
                            element={
                              <CampaignsSection>
                                <CampaignAnalytics />
                              </CampaignsSection>
                            }
                          />
                          <Route
                            path="/reach/automatic"
                            element={
                              <ReachSection>
                                <ReachCampaigns mode="automatic" />
                              </ReachSection>
                            }
                          />
                          <Route path="/reach/campaigns" element={<MovedTo to="/campaigns" />} />
                          <Route
                            path="/reach/campaigns/manage"
                            element={
                              <ReachSection>
                                <ProjectsLayout />
                              </ReachSection>
                            }
                          />
                          {/* ── Collect shell ───────────────────────────────── */}
                          <Route
                            path="/collection"
                            element={
                              <ProductRoute product="collect">
                                <CollectionLayout />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/collection/:id"
                            element={
                              <ProductRoute product="collect">
                                <CollectionItemDetail />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/capture"
                            element={
                              <ProductRoute product="collect">
                                <CaptureLayout />
                              </ProductRoute>
                            }
                          />
                          {/* tinycms shell. The atomizer id is that shell; the old
                            /cms paths redirect onto it. */}
                          <Route
                            path="/today"
                            element={
                              <ProductRoute product="atomizer">
                                <CmsToday />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/sites"
                            element={
                              <ProductRoute product="atomizer">
                                <CmsSites />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/reach/analytics"
                            element={
                              <ReachSection>
                                <ReachAnalytics />
                              </ReachSection>
                            }
                          />
                          {/* Reach (Calendar · Automatic · Channels · Analytics), Campaigns and
                            Prism AI replace Add-ons and the old Amplify dashboard. */}
                          <Route
                            path="/reach/channels"
                            element={
                              <ReachSection>
                                <ReachChannels />
                              </ReachSection>
                            }
                          />
                          {/* Addresses match the tabs (Reach → Calendar · Automatic ·
                            Channels, Prism AI → Research · Write); the old ones
                            redirect, keeping their query and #section. */}
                          <Route path="/reach" element={<MovedTo to="/reach/calendar" />} />
                          <Route path="/calendar" element={<MovedTo to="/reach/calendar" />} />
                          <Route path="/amplify" element={<MovedTo to="/reach/calendar" />} />
                          <Route path="/projects" element={<MovedTo to="/campaigns" />} />
                          <Route
                            path="/projects/manage"
                            element={<MovedTo to="/reach/campaigns/manage" />}
                          />
                          <Route path="/channels" element={<MovedTo to="/reach/channels" />} />
                          <Route path="/prism-ai" element={<MovedTo to="/prism-ai/research" />} />
                          <Route path="/research" element={<MovedTo to="/prism-ai/research" />} />
                          <Route path="/assembler" element={<MovedTo to="/prism-ai/write" />} />
                          {/* Assembler is Write since 2026-10-23; its old address redirects. */}
                          <Route
                            path="/prism-ai/assembler"
                            element={<MovedTo to="/prism-ai/write" />}
                          />
                          {/* Prism AI was Atomize until 2026-10-19; its old addresses redirect. */}
                          <Route path="/atomize" element={<MovedTo to="/prism-ai/research" />} />
                          <Route
                            path="/atomize/research"
                            element={<MovedTo to="/prism-ai/research" />}
                          />
                          <Route
                            path="/atomize/assembler"
                            element={<MovedTo to="/prism-ai/write" />}
                          />
                          <Route path="/addons" element={<AddonsRedirect />} />
                          <Route path="/cms" element={<Navigate to="/today" replace />} />
                          <Route
                            path="/cms/articles"
                            element={<Navigate to="/content" replace />}
                          />
                          <Route path="/cms/articles/:id" element={<CmsArticleRedirect />} />
                          <Route
                            path="/cms/content-settings"
                            element={<Navigate to="/content/settings" replace />}
                          />
                          <Route path="/cms/site" element={<Navigate to="/site" replace />} />
                          <Route path="/cms/addons" element={<Navigate to="/addons" replace />} />
                          {/* Approval history is Account → Activity log ▸ Approvals now (v14). */}
                          <Route
                            path="/cms/history"
                            element={<Navigate to="/settings/activity?filter=approval" replace />}
                          />
                          {/* Collect keeps Media as its own place. Atomizer folds it
                            into Content, as Articles | Media. */}
                          <Route path="/media" element={<MediaRoute />} />
                          {/* ── Content Engine shell ────────────────────────── */}
                          <Route
                            path="/prism-ai/settings"
                            element={
                              <ProductRoute product="atomizer">
                                <PrismAiSettings />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/prism-ai/research"
                            element={
                              <ProductRoute product="atomizer">
                                <AtomizeSection>
                                  <ResearchLayout />
                                </AtomizeSection>
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/prism-ai/write"
                            element={
                              <ProductRoute product="atomizer">
                                <AtomizeSection>
                                  <AssemblerLayout />
                                </AtomizeSection>
                              </ProductRoute>
                            }
                          />
                          {/* The site's look is Content → Design; the rest is Site settings. */}
                          <Route path="/site" element={<SiteRedirect />} />
                          {/* Site-wide settings live in Content → Manage. Articles and Forms
                              have their own, from a Settings button on their tab (46b884ba). */}
                          <Route
                            path="/content/media/settings"
                            element={<Navigate to="/content?view=media" replace />}
                          />
                          <Route path="/content/forms/settings" element={<CmsFormSettings />} />
                          <Route path="/site/settings" element={<ManageRedirect />} />
                          <Route path="/content/settings" element={<ContentSettings />} />
                          <Route path="/manage" element={<ManageRedirect area="site" />} />
                          <Route
                            path="/content"
                            element={
                              <ProductRoute product="atomizer">
                                <ContentLayout />
                              </ProductRoute>
                            }
                          />
                          {/* /content/forms etc. read as a tab, not an article id: they
                              opened "This item isn't available" (UX review round 8). */}
                          {['pages', 'articles', 'forms', 'media', 'design', 'analytics'].map(
                            (view) => (
                              <Route
                                key={view}
                                path={`/content/${view}`}
                                element={<Navigate to={`/content?view=${view}`} replace />}
                              />
                            )
                          )}
                          {/* New article: the editor with nothing saved until Create draft. */}
                          <Route
                            path="/content/new"
                            element={
                              <ProductRoute product="atomizer">
                                {/* Its own key: Create draft opens the saved article fresh, not still editing. */}
                                <ContentDetail key="new" isNew />
                              </ProductRoute>
                            }
                          />
                          <Route
                            path="/content/:id"
                            element={
                              <ProductRoute product="atomizer">
                                <ContentDetail />
                              </ProductRoute>
                            }
                          />
                          {/* Profile settings is its own page (2026-10-24); /settings/profile
                              redirects here (SettingsLayout). */}
                          <Route path="/profile" element={<ProfilePage />} />
                          <Route path="/settings" element={<SettingsLayout />}>
                            {/* /settings/general, /team, …: the layout draws the section itself. */}
                            <Route path=":section" element={null} />
                          </Route>
                        </Route>
                        {/* Add other protected routes here */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </BrowserRouter>
                  </ErrorBoundary>
                </ProfileSelectionProvider>
              </SidebarProvider>
            </ProductProvider>
          </AccountProvider>
        </ThemeProvider>
      </AuthProvider>
    </ApolloProvider>
  );
}

export default App;
